import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logoImage from "../../assets/logo-prima-hitam.png";
import moment from "moment";
import "moment/locale/id";
import { useSelector } from "react-redux";

moment.locale("id");

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 9,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },

  /* ================= HEADER ================= */
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
    paddingBottom: 10,
    borderBottom: "2px solid #333",
  },

  headerLeft: {
    width: "60%",
  },

  headerRight: {
    width: "40%",
    alignItems: "flex-end",
  },

  logo: {
    width: 80,
    marginBottom: 5,
  },

  documentTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 4,
    letterSpacing: 1,
  },

  documentSubtitle: {
    fontSize: 10,
    color: "#666",
  },

  /* ================= TABLE ================= */
  table: {
    width: "100%",
    marginTop: 10,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    borderBottomWidth: 1,
    borderBottomColor: "#333",
    paddingVertical: 6,
    paddingHorizontal: 4,
  },

  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "center",
    color: "#333",
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ccc",
    paddingVertical: 5,
    paddingHorizontal: 4,
  },

  tableRowEven: {
    backgroundColor: "#fafafa",
  },

  /* Column widths */
  colNo: { width: "5%" },
  colId: { width: "12%" },
  colNama: { width: "18%" },
  colMonth: { width: "7%" },

  tableCell: {
    fontSize: 8,
    textAlign: "center",
  },

  tableCellLeft: {
    fontSize: 8,
    textAlign: "left",
  },

  /* ================= GRAND TOTAL ================= */
  grandTotalRow: {
    flexDirection: "row",
    backgroundColor: "#333",
    paddingVertical: 8,
    paddingHorizontal: 4,
    marginTop: 5,
  },

  grandTotalLabel: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#fff",
    textAlign: "left",
  },

  grandTotalValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#fff",
    textAlign: "right",
  },

  /* ================= FOOTER ================= */
  footer: {
    marginTop: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
  },

  footerText: {
    fontSize: 8,
    color: "#666",
    textAlign: "right",
  },
});

const CetakRekapTabunganDocument = ({ data, selectedYear, grandTotal }) => {
  const formatRupiah = (value) => {
    if (!value || value === "0" || value === "0.00") return "-";
    const num = parseFloat(value);
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(num);
  };

  // Month names
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image src={logoImage} alt="PRIMA Centre Logo" style={styles.logo} />
            <Text style={{ fontSize: 9, color: "#666", marginTop: 2 }}>
              Jl. RE. Martadinata No.48, Cipaning, Kec. Ciborong,{"\n"}
              Kota Sukabumi, Jawa Barat 43122
            </Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.documentTitle}>REKAP TABUNGAN BULANAN</Text>
            <Text style={styles.documentSubtitle}>
              Tahun {selectedYear}
            </Text>
            <Text style={{ fontSize: 8, color: "#888", marginTop: 5 }}>
              Dicetak: {moment().format("DD MMMM YYYY")}
            </Text>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.colNo]}>No</Text>
            <Text style={[styles.tableHeaderCell, styles.colId]}>ID Karyawan</Text>
            <Text style={[styles.tableHeaderCell, styles.colNama]}>Nama Karyawan</Text>
            {monthNames.map((month, index) => (
              <Text key={index} style={[styles.tableHeaderCell, styles.colMonth]}>
                {month}
              </Text>
            ))}
            <Text style={[styles.tableHeaderCell, { width: "12%" }]}>Total</Text>
          </View>

          {/* Table Body */}
          {data.map((row, index) => (
            <View
              key={index}
              style={[
                styles.tableRow,
                index % 2 === 1 && styles.tableRowEven,
              ]}
            >
              <Text style={[styles.tableCell, styles.colNo]}>
                {index + 1}
              </Text>
              <Text style={[styles.tableCellLeft, styles.colId]}>
                {row.id_karyawan}
              </Text>
              <Text style={[styles.tableCellLeft, styles.colNama]}>
                {row.nama_karyawan}
              </Text>
              {monthNames.map((_, monthIndex) => {
                const value = row[`bulan_${monthIndex + 1}`];
                return (
                  <Text key={monthIndex} style={[styles.tableCell, styles.colMonth]}>
                    {value && value !== "0.00" && value !== "0" 
                      ? formatRupiah(value) 
                      : "-"}
                  </Text>
                );
              })}
              <Text style={[styles.tableCell, { width: "12%", fontWeight: "bold" }]}>
                {formatRupiah(row.total_saldo)}
              </Text>
            </View>
          ))}
        </View>

        {/* Grand Total */}
        <View style={styles.grandTotalRow}>
          <Text style={[styles.grandTotalLabel, { width: "35%" }]}>
            GRAND TOTAL
          </Text>
          <View style={{ width: "65%", flexDirection: "row" }}>
            {monthNames.map((_, index) => (
              <Text key={index} style={[styles.grandTotalValue, styles.colMonth]}>
                
              </Text>
            ))}
            <Text style={[styles.grandTotalValue, { width: "12%" }]}>
              {formatRupiah(grandTotal)}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Dokumen ini dicetak secara otomatis dari sistem PRIMA Centre
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const CetakRekapTabungan = () => {
  const { list_rekap_tabungan_karyawan } = useSelector((state) => state.karyawan);
  const [selectedYear, setSelectedYear] = useState("");
  const [data, setData] = useState([]);

  useEffect(() => {
    const data_source = sessionStorage.getItem("datacetakrekaptabungan");
    if (data_source) {
      const parsed = JSON.parse(data_source);
      setData(parsed.data || []);
      setSelectedYear(parsed.year || new Date().getFullYear());
    }
  }, []);

  // Calculate grand total (use last month with data for each employee)
  const grandTotal = useMemo(() => {
    return data.reduce((sum, row) => {
      // Find the last month that has a value
      let lastValue = 0;
      for (let i = 12; i >= 1; i--) {
        const bulanValue = row[`bulan_${i}`];
        if (bulanValue && parseFloat(bulanValue) > 0) {
          lastValue = parseFloat(bulanValue);
          break;
        }
      }
      return sum + lastValue;
    }, 0);
  }, [data]);

  return (
    <div style={{ height: "100vh" }}>
      <PDFViewer width="100%" height="100%">
        <CetakRekapTabunganDocument 
          data={data} 
          selectedYear={selectedYear}
          grandTotal={grandTotal}
        />
      </PDFViewer>
    </div>
  );
};

export default CetakRekapTabungan;
